'use client';

/**
 * MediaPicker — unified library-pick + upload dialog.
 *
 * Mirrors the legacy Meteor app's `MediaSelector`: open a dialog, pick from
 * the dancer's existing `artist_media` library or upload a new file, return
 * the selected media id(s). The only thing that changes between surfaces is
 * the `types` filter ("show me videos only", "show me PDFs only", …).
 *
 * Architecture:
 *   - Library tab calls `loadLibrary()` (provided by caller) once on open
 *     and caches in component state. Filter is applied in-memory by `types`.
 *   - Upload tab embeds the existing `MediaUploader` configured with the
 *     caller's `uploadAction`. Newly-uploaded rows are prepended to the
 *     library list and (in single mode) auto-selected.
 *   - Returns media ids only — the caller is responsible for resolving full
 *     row data when it needs to render. Library results carry enough fields
 *     for the picker UI to show thumbnails without an extra round-trip.
 *
 * Why a thin wrapper, not a re-implementation: the 3-step direct-upload
 * flow already lives in `uploadDancerMedia` and knows how to bypass
 * Vercel's 4.5 MB body cap, MIME-validate, and handle headshot uniqueness.
 * Re-implementing it inside a dialog would duplicate ~400 LOC.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { Check, Loader2 } from 'lucide-react';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@kit/ui/tabs';
import { cn } from '@kit/ui/utils';

import { type MediaFile, type UploadResult } from '../hooks/use-media-upload';
import {
  libraryItemToMediaFile,
  SelectedInlineCard,
  SelectedThumbRow,
} from './media-picker-selection';
import { MediaThumb } from './media-thumb';
import { MediaUploader } from './media-uploader';

/**
 * The media kinds the picker can filter on. Aligned with `artist_media.media_type`
 * plus a synthetic `'pdf'` alias that maps to mime_type='application/pdf'.
 */
export type PickerMediaType =
  | 'headshot'
  | 'photo'
  | 'video'
  | 'document'
  | 'pdf';

/**
 * Library row shape the caller hands to the picker. Designed to match the
 * `DancerMedia` interface from `@kit/artists` 1:1, but kept loose so other
 * media sources (shared media libraries, future) can plug in without
 * pulling in the dancers package.
 */
export interface MediaPickerLibraryItem {
  id: string;
  media_type: string;
  storage_path: string | null;
  external_url: string | null;
  title: string | null;
  description: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  /**
   * Pre-resolved signed URL for thumbnail. Optional — when null and
   * storage_path looks already-signed (URL-shaped), MediaThumb will render
   * it directly. `getMediaByUser` from DancerMediaService already returns
   * signed URLs in `storage_path` so this is redundant for that path.
   */
  signed_url?: string | null;
  // Video residency signals (BAL-721 / video-playback-resolver). Without these
  // the picker cluster is structurally incapable of routing a migrated video to
  // Mux/R2 and renders the dead Supabase-Storage URL (the confirmed apply-preview
  // 400). Threaded from `DancerMedia` (getMediaByUser does `select('*')`) into
  // `MediaFile` so `MediaThumb`/`MediaLightbox` resolve the live source.
  provider?: string | null;
  mux_playback_id?: string | null;
  mux_status?: string | null;
  r2_key?: string | null;
}

export interface MediaPickerTranslations {
  triggerLabel?: string;
  /**
   * Trigger label used when at least one item is already selected. Falls
   * back to `triggerLabel` when not provided. Lets multi-mode pickers
   * surface "Add more" affordance and single-mode pickers surface a
   * "Replace" verb on the inline card's primary button.
   */
  triggerLabelHasSelection?: string;
  dialogTitle?: string;
  dialogDescription?: string;
  libraryTab?: string;
  uploadTab?: string;
  emptyLibrary?: string;
  loading?: string;
  cancel?: string;
  confirm?: string;
  noTypeMatch?: string;
  removeSelectedAria?: string;
  /**
   * Confirmation shown before deleting a media row that is referenced by
   * other applications. `{count}` is replaced with the usage count and
   * `{plural}` with the consumer-provided plural suffix (default `s`).
   */
  deleteUsageConfirm?: string;
  /** Plural suffix interpolated into `deleteUsageConfirm` when count !== 1. */
  deleteUsagePluralSuffix?: string;
}

const DEFAULT_TRANSLATIONS: Required<MediaPickerTranslations> = {
  triggerLabel: 'Select a file',
  triggerLabelHasSelection: '',
  dialogTitle: 'Select media',
  dialogDescription:
    'Pick from your media library or upload a new file. Files appear here once they are saved to your profile.',
  libraryTab: 'Your library',
  uploadTab: 'Upload new',
  emptyLibrary: 'Nothing here yet — switch to "Upload new" to add a file.',
  loading: 'Loading…',
  cancel: 'Cancel',
  confirm: 'Use selected',
  noTypeMatch:
    'No matching files in your library. Switch to "Upload new" to add one.',
  removeSelectedAria: 'Remove from selection',
  deleteUsageConfirm:
    'This file is used in {count} application{plural}. Deleting it removes the link from those applications too. Continue?',
  deleteUsagePluralSuffix: 's',
};

export interface MediaPickerProps {
  /** Filter the library by these media types. */
  types: PickerMediaType[];

  /** Single-select returns one id; multi returns an array. */
  mode: 'single' | 'multiple';

  /** Currently-selected media id(s). */
  value: string | string[] | null | undefined;

  /** Called with the new selection when the user confirms. */
  onChange: (value: string | string[] | null) => void;

  /**
   * The `artist_media.media_type` to use when uploading from this picker.
   * Drives which validation path the server runs (size cap, MIME allowlist,
   * uniqueness checks). Required because `types` is a *filter* and may
   * include multiple values.
   */
  mediaTypeForUpload: 'headshot' | 'photo' | 'video' | 'document';

  /** Allowed MIME types for the upload tab. Defaults derived from `types`. */
  allowedTypesForUpload?: string[];

  /** Max file size for the upload tab, in bytes. Defaults derived from `types`. */
  maxFileSizeForUpload?: number;

  /**
   * Server action wrapped to fit the MediaUploader's FormData contract.
   * Typically built from `uploadDancerMedia` in the web app.
   */
  uploadAction: (formData: FormData) => Promise<UploadResult>;

  /** Optional delete action — wires through to the library's row-actions menu. */
  deleteAction?: (params: { mediaId: string }) => Promise<UploadResult>;

  /**
   * Optional reuse-check action. When provided, the picker calls this before
   * actually deleting and surfaces a confirmation prompt if the file is
   * referenced by other objects (e.g. existing applications). Returning
   * `count: 0` skips the prompt; `count: N` triggers it.
   */
  getUsage?: (
    mediaId: string,
  ) => Promise<
    { success: true; count: number } | { success: false; error: string }
  >;

  /**
   * Optional metadata-update action. When provided, every library tile
   * gets an "Edit title/description" affordance.
   */
  updateMeta?: (input: {
    mediaId: string;
    title?: string | null;
    description?: string | null;
  }) => Promise<
    | {
        success: true;
        data: { id: string; title: string | null; description: string | null };
      }
    | { success: false; error: string }
  >;

  /**
   * Loads the library on dialog open. Returns the full library — the picker
   * filters in-memory by `types`. Re-run via `key` change if the consumer
   * needs to refresh.
   */
  loadLibrary: () => Promise<MediaPickerLibraryItem[]>;

  /**
   * Optional notification fired when an upload inside the picker creates a
   * brand-new `dancer_media` row. Lets the caller mirror the new row into
   * its own library state so subsequent preview lookups (`media.find(...)`)
   * succeed without a server round-trip. The picker has already added the
   * item to its internal library before this fires.
   */
  onLibraryItemAdded?: (item: MediaPickerLibraryItem) => void;

  /** Multi-select cap; only meaningful when mode='multiple'. */
  maxSelected?: number;

  /** Custom button rendered as the dialog trigger (default: outline button). */
  trigger?: React.ReactNode;

  /**
   * Pre-resolved library rows for ids in `value` so the picker can render
   * thumbnails BEFORE the user opens the dialog (the internal library
   * cache is only loaded on dialog open). Pass your local cache here
   * when you have it; omit otherwise — uploaded items already auto-merge
   * into the picker's cache via the upload tab's onUploadComplete.
   */
  selectedItemsHint?: MediaPickerLibraryItem[];

  /**
   * Render mode for the current selection.
   *  - `'inline'` (default when `mode='single'`): selection REPLACES the
   *    trigger with a card showing the thumb, a Replace button (re-opens
   *    the picker), and a Remove button.
   *  - `'above'` (default when `mode='multiple'`): selection renders as a
   *    thumbnail row ABOVE the trigger; trigger label flips to
   *    `triggerLabelHasSelection` (or stays as `triggerLabel` if not set).
   *  - `'none'`: no preview, trigger always rendered. Auto-applied when
   *    caller passes a custom `trigger`.
   */
  selectionDisplay?: 'inline' | 'above' | 'none';

  /**
   * Optional handler for the per-thumbnail Remove (X) button. Receives
   * the removed id and the post-removal value (matches the `onChange`
   * shape: string|null in single mode, string[] in multiple mode). If
   * omitted, the picker calls `onChange(nextValue)` instead.
   */
  onRemoveSelected?: (id: string, nextValue: string | string[] | null) => void;

  /**
   * Optional renderer called inside the inline-mode selected card to add
   * caller-specific metadata below the item title — file size, preview
   * link, "Open original" anchor, etc. Only used when
   * `selectionDisplay === 'inline'`. Receives the resolved library item.
   * Keep the output compact (one or two lines) so the card stays single-row.
   */
  renderSelectedExtras?: (item: MediaPickerLibraryItem) => React.ReactNode;

  /** Disables the trigger and the inner UI. */
  disabled?: boolean;

  /** Optional className applied to the dialog content. */
  className?: string;

  translations?: MediaPickerTranslations;
}

/**
 * Returns true when `item.media_type` (or the special PDF mime alias) is
 * in the `types` filter.
 */
function matchesTypes(
  item: MediaPickerLibraryItem,
  types: PickerMediaType[],
): boolean {
  if (types.length === 0) return true;
  if (types.includes(item.media_type as PickerMediaType)) return true;
  if (types.includes('pdf') && item.mime_type === 'application/pdf') {
    return true;
  }
  return false;
}

/**
 * Maps the picker's `types` filter to a default MIME allowlist for the
 * upload tab. Callers can override with `allowedTypesForUpload`.
 */
function defaultAllowedTypes(
  mediaTypeForUpload: MediaPickerProps['mediaTypeForUpload'],
): string[] {
  switch (mediaTypeForUpload) {
    case 'headshot':
    case 'photo':
      return ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    case 'video':
      return ['video/mp4', 'video/quicktime', 'video/webm'];
    case 'document':
      return ['application/pdf'];
  }
}

function defaultMaxSize(
  mediaTypeForUpload: MediaPickerProps['mediaTypeForUpload'],
): number {
  switch (mediaTypeForUpload) {
    case 'headshot':
      return 5 * 1024 * 1024;
    case 'photo':
      return 10 * 1024 * 1024;
    case 'video':
      return 200 * 1024 * 1024;
    case 'document':
      return 10 * 1024 * 1024;
  }
}

export function MediaPicker({
  types,
  mode,
  value,
  onChange,
  mediaTypeForUpload,
  allowedTypesForUpload,
  maxFileSizeForUpload,
  uploadAction,
  deleteAction,
  getUsage,
  loadLibrary,
  onLibraryItemAdded,
  maxSelected,
  trigger,
  selectedItemsHint,
  selectionDisplay: selectionDisplayProp,
  onRemoveSelected,
  renderSelectedExtras,
  disabled,
  className,
  translations: customTranslations,
}: MediaPickerProps) {
  const t = { ...DEFAULT_TRANSLATIONS, ...customTranslations };
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'library' | 'upload'>('library');
  const [library, setLibrary] = useState<MediaPickerLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  // Draft selection mirrored separately from `value` so the user can change
  // their mind and Cancel without firing onChange.
  const [draft, setDraft] = useState<string[]>([]);

  // Caller-supplied trigger means caller owns the surrounding UI — never
  // overlay our preview on top of their custom button.
  const selectionDisplay: 'inline' | 'above' | 'none' =
    selectionDisplayProp ??
    (trigger ? 'none' : mode === 'single' ? 'inline' : 'above');

  // Resolve current `value` ids to library rows. Source priority:
  // selectedItemsHint (parent cache) > internal library (loaded on open).
  // The hint covers first-paint; the library covers items added via the
  // dialog's upload tab (which prepends to `library`).
  const valueIds = useMemo<string[]>(
    () =>
      value == null
        ? []
        : Array.isArray(value)
          ? value.filter(Boolean)
          : [value].filter(Boolean),
    [value],
  );

  const resolvedSelection = useMemo<MediaPickerLibraryItem[]>(() => {
    if (valueIds.length === 0) return [];
    const map = new Map<string, MediaPickerLibraryItem>();
    if (selectedItemsHint) {
      for (const item of selectedItemsHint) map.set(item.id, item);
    }
    for (const item of library) map.set(item.id, item);
    const out: MediaPickerLibraryItem[] = [];
    for (const id of valueIds) {
      const found = map.get(id);
      if (found) out.push(found);
    }
    return out;
  }, [valueIds, selectedItemsHint, library]);

  const hasSelection = resolvedSelection.length > 0;

  // Sync draft when dialog opens. Wrapped in startTransition so the setState
  // calls are deferred (non-urgent) and don't cascade synchronously.
  useEffect(() => {
    if (!open) return;
    startTransition(() => {
      if (value == null) setDraft([]);
      else if (Array.isArray(value)) setDraft(value.filter(Boolean));
      else setDraft([value].filter(Boolean));
    });
  }, [open, value, startTransition]);

  // Load the library on open. The initial setLoading(true)/setError(null) are
  // wrapped in startTransition to avoid synchronous setState in effect body.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    startTransition(() => {
      setLoading(true);
      setError(null);
    });
    loadLibrary()
      .then((rows) => {
        if (!cancelled) setLibrary(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, loadLibrary, startTransition]);

  const filteredLibrary = useMemo(
    () => library.filter((item) => matchesTypes(item, types)),
    [library, types],
  );

  const toggleId = useCallback(
    (id: string) => {
      if (mode === 'single') {
        setDraft([id]);
        return;
      }
      setDraft((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id);
        if (maxSelected != null && prev.length >= maxSelected) return prev;
        return [...prev, id];
      });
    },
    [mode, maxSelected],
  );

  const handleConfirm = () => {
    if (mode === 'single') {
      onChange(draft[0] ?? null);
    } else {
      onChange(draft);
    }
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  /**
   * Called by MediaUploader when an upload completes. Prepend the new row
   * to the library state, jump back to the Library tab, and (single mode)
   * auto-select it. The shape coming back from MediaUploader is `MediaFile`,
   * not the full DancerMedia row — but for picker selection we only need
   * the id.
   */
  const handleUploadComplete = useCallback(
    (file: MediaFile) => {
      const newItem: MediaPickerLibraryItem = {
        id: file.id,
        media_type: mediaTypeForUpload,
        storage_path: file.storagePath ?? null,
        external_url: file.externalUrl ?? null,
        title: file.title ?? file.fileName ?? null,
        description: null,
        mime_type: file.mimeType ?? null,
        file_size_bytes: file.fileSize ?? null,
        signed_url: file.signedUrl ?? null,
        // A just-uploaded video has no Mux/R2 residency yet (encode is queued),
        // so these are usually null; carried through so a re-selected library
        // row that DOES have them routes to the live source.
        provider: null,
        mux_playback_id: file.muxPlaybackId ?? null,
        mux_status: file.muxStatus ?? null,
        r2_key: file.r2Key ?? null,
      };
      setLibrary((prev) => [newItem, ...prev]);
      onLibraryItemAdded?.(newItem);
      // Auto-select.
      if (mode === 'single') {
        setDraft([newItem.id]);
      } else {
        setDraft((prev) =>
          prev.includes(newItem.id) ? prev : [...prev, newItem.id],
        );
      }
      setTab('library');
    },
    [mediaTypeForUpload, mode, onLibraryItemAdded],
  );

  const { deleteUsageConfirm, deleteUsagePluralSuffix } = t;

  const handleLibraryDelete = useCallback(
    async (mediaId: string) => {
      if (!deleteAction) return;

      // Reuse-check: warn before deleting a media row referenced by other
      // applications. The action is RLS-scoped to the caller's own apps.
      if (getUsage) {
        try {
          const usage = await getUsage(mediaId);
          if (usage.success && usage.count > 0) {
            const plural = usage.count === 1 ? '' : deleteUsagePluralSuffix;
            const message = deleteUsageConfirm
              .replace('{count}', String(usage.count))
              .replace('{plural}', plural);
            const ok = window.confirm(message);
            if (!ok) return;
          }
        } catch {
          // best-effort — don't block delete on a usage-check failure
        }
      }

      const result = await deleteAction({ mediaId });
      if (result.success) {
        setLibrary((prev) => prev.filter((m) => m.id !== mediaId));
        setDraft((prev) => prev.filter((id) => id !== mediaId));
      }
    },
    [deleteAction, getUsage, deleteUsageConfirm, deleteUsagePluralSuffix],
  );

  const handleRemoveSelected = useCallback(
    (id: string) => {
      const nextIds = valueIds.filter((existing) => existing !== id);
      const nextValue: string | string[] | null =
        mode === 'single' ? (nextIds[0] ?? null) : nextIds;
      if (onRemoveSelected) {
        onRemoveSelected(id, nextValue);
      } else {
        onChange(nextValue);
      }
    },
    [valueIds, mode, onRemoveSelected, onChange],
  );

  const allowedTypes =
    allowedTypesForUpload ?? defaultAllowedTypes(mediaTypeForUpload);
  const maxFileSize =
    maxFileSizeForUpload ?? defaultMaxSize(mediaTypeForUpload);

  const triggerLabelForState =
    hasSelection && t.triggerLabelHasSelection
      ? t.triggerLabelHasSelection
      : t.triggerLabel;

  // Render the trigger as a plain button bound directly to setOpen — Radix's
  // <DialogTrigger> wrapper would complicate the inline-mode flow (where the
  // Replace button already drives setOpen) and gates the trigger on
  // dialog-open in some test mocks. Going direct keeps a single open-path
  // everywhere and stays semantically identical (a button that toggles
  // dialog state). For a caller-supplied custom trigger, we clone-inject
  // the onClick so the caller's element opens the dialog without needing
  // to know about the picker's internal state.
  const openOnClick = useCallback(() => setOpen(true), []);
  const renderTrigger = trigger ? (
    React.isValidElement(trigger) ? (
      React.cloneElement(
        trigger as React.ReactElement<{ onClick?: () => void }>,
        { onClick: openOnClick },
      )
    ) : (
      trigger
    )
  ) : (
    <Button
      type="button"
      variant="outline"
      disabled={disabled}
      onClick={openOnClick}
    >
      {triggerLabelForState}
    </Button>
  );

  // Inline mode: when something is picked AND no caller trigger override,
  // hide the trigger entirely and render a card with thumb + Replace +
  // Remove. Picker stays mounted so the Replace button can still open the
  // dialog via setOpen.
  if (selectionDisplay === 'inline' && hasSelection) {
    const item = resolvedSelection[0]!;
    return (
      <>
        <SelectedInlineCard
          item={item}
          replaceLabel={triggerLabelForState}
          removeAria={t.removeSelectedAria}
          disabled={disabled}
          extras={renderSelectedExtras?.(item)}
          onReplace={() => setOpen(true)}
          onRemove={() => handleRemoveSelected(item.id)}
        />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className={cn('max-w-3xl', className)}>
            {renderDialogBody()}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      {selectionDisplay === 'above' && hasSelection && (
        <SelectedThumbRow
          items={resolvedSelection}
          removeAria={t.removeSelectedAria}
          disabled={disabled}
          onRemove={handleRemoveSelected}
          className="mb-3"
        />
      )}
      {renderTrigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={cn('max-w-3xl', className)}>
          {renderDialogBody()}
        </DialogContent>
      </Dialog>
    </>
  );

  function renderDialogBody() {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{t.dialogTitle}</DialogTitle>
          <DialogDescription>{t.dialogDescription}</DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as 'library' | 'upload')}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="library">{t.libraryTab}</TabsTrigger>
            <TabsTrigger value="upload">{t.uploadTab}</TabsTrigger>
          </TabsList>

          <TabsContent
            value="library"
            className="max-h-[50vh] min-h-[200px] overflow-y-auto"
          >
            {loading && (
              <div className="text-muted-foreground flex items-center justify-center gap-2 py-12 text-sm">
                <Loader2 className="size-4 animate-spin" />
                {t.loading}
              </div>
            )}

            {!loading && error && (
              <div className="text-destructive py-12 text-center text-sm">
                {error}
              </div>
            )}

            {!loading && !error && filteredLibrary.length === 0 && (
              <div className="text-muted-foreground py-12 text-center text-sm">
                {library.length === 0 ? t.emptyLibrary : t.noTypeMatch}
              </div>
            )}

            {!loading && !error && filteredLibrary.length > 0 && (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                {filteredLibrary.map((item) => {
                  const isSelected = draft.includes(item.id);
                  return (
                    // eslint-disable-next-line kit-components/use-kit-primitives -- thumbnail-grid tile wrapping MediaThumb; Button's fixed h-9 sizing would collapse the media thumbnail and the size chrome can't be dropped via variant=ghost
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleId(item.id)}
                      className={cn(
                        'group relative cursor-pointer rounded-md border-2 transition-all',
                        isSelected
                          ? 'border-primary ring-primary/20 ring-2'
                          : 'border-border/50 hover:border-foreground/40',
                      )}
                      aria-pressed={isSelected}
                      aria-label={
                        item.title ?? `Select ${item.media_type} ${item.id}`
                      }
                    >
                      <MediaThumb
                        file={libraryItemToMediaFile(item)}
                        onDelete={
                          deleteAction
                            ? () => void handleLibraryDelete(item.id)
                            : undefined
                        }
                      />
                      {isSelected && (
                        <div className="bg-primary text-primary-foreground absolute top-1 right-1 z-10 flex size-6 items-center justify-center rounded-full shadow">
                          <Check className="size-3.5" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="upload" className="min-h-[200px]">
            <MediaUploader
              mode="single"
              allowedTypes={allowedTypes}
              maxFileSize={maxFileSize}
              uploadAction={uploadAction}
              additionalData={{ mediaType: mediaTypeForUpload }}
              onUploadComplete={handleUploadComplete}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={handleCancel}>
            {t.cancel}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={mode === 'single' ? draft.length === 0 : false}
          >
            {t.confirm}
            {mode === 'multiple' &&
              draft.length > 0 &&
              ` (${draft.length}${maxSelected ? `/${maxSelected}` : ''})`}
          </Button>
        </DialogFooter>
      </>
    );
  }
}
